
import { jsPDF } from 'jspdf';
import { Video, AiAnalysis, Kpi, Company } from '../types';

const formatKpiKey = (key: string): string => {
    return key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result as string;
            resolve(result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

const getBase64FromUrl = async (url: string): Promise<string> => {
    if (!url) return "";

    const fetchImage = async (fetchUrl: string) => {
        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`);
        const blob = await response.blob();
        return await blobToBase64(blob);
    };
    
    try {
        return await fetchImage(url);
    } catch (e1) {
        console.warn(`Direct fetch for PDF logo failed (${url}), trying proxy 1 (allorigins)...`);
        try {
             const proxyUrl1 = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
             return await fetchImage(proxyUrl1);
        } catch (e2) {
            console.warn(`Proxy 1 failed for PDF logo, trying proxy 2 (corsproxy)...`);
             try {
                 const proxyUrl2 = `https://corsproxy.io/?${encodeURIComponent(url)}`;
                 return await fetchImage(proxyUrl2);
             } catch (e3) {
                 console.error(`All attempts to fetch PDF logo failed for ${url}`, e3);
                 return "";
             }
        }
    }
};

const getFormatFromDataUrl = (dataUrl: string): string => {
    if (!dataUrl || dataUrl === "") return 'PNG';
    const match = dataUrl.match(/data:image\/(\w+);base64/);
    if (match && match[1]) {
        let fmt = match[1].toUpperCase();
        if (fmt === 'JPG') fmt = 'JPEG';
        if (['JPEG', 'PNG', 'WEBP', 'BMP', 'GIF'].includes(fmt)) {
            return fmt;
        }
    }
    return 'PNG'; 
};

const drawHeader = (doc: jsPDF, logos: { left: string; right: string }) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    let headerY = 20;

    const { left, right } = logos;
    const logoWidth = 35; 
    const logoHeight = 20; 
    
    if (left) {
        try {
            const format = getFormatFromDataUrl(left);
            doc.addImage(left, format, 15, 10, logoWidth, logoHeight, undefined, 'FAST');
        } catch (e) {
            console.warn("Could not add left logo to PDF", e);
        }
    }
    
    if (right) {
        try {
            const format = getFormatFromDataUrl(right);
            doc.addImage(right, format, pageWidth - 15 - logoWidth, 10, logoWidth, logoHeight, undefined, 'FAST');
        } catch (e) {
            console.warn("Could not add right logo to PDF", e);
        }
    }
    
    if (left || right) {
        headerY = 35; 
    }
    
    return headerY;
};

const cleanMarkdown = (text: string): string => {
    if (!text) return "";
    return text
        .replace(/\*\*(.*?)\*\*/g, '$1') 
        .replace(/\*(.*?)\*/g, '$1')     
        .replace(/^#+\s/gm, '')          
        .replace(/`/g, '')               
        .replace(/\n\s*-\s/g, '\n• ')    
        .replace(/\n\s*\*\s/g, '\n• ');  
};

export const generateAnalysisPdf = async (
    video: Video,
    analysis: AiAnalysis,
    chartImages?: Record<string, string>,
    company?: Company
) => {
    const doc = new jsPDF();
    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    
    const logos = { left: '', right: '' };
    if (company?.reportSettings?.headerLogos) {
        const { left, right } = company.reportSettings.headerLogos;
        const [leftBase64, rightBase64] = await Promise.all([
             left ? getBase64FromUrl(left) : Promise.resolve(''),
             right ? getBase64FromUrl(right) : Promise.resolve('')
        ]);
        logos.left = leftBase64;
        logos.right = rightBase64;
    }

    let y = drawHeader(doc, logos);

    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('MysteryShopperIA Analysis Report', pageWidth / 2, y, { align: 'center' });
    y += 15;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Video Information', margin, y);
    y += 8;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    doc.setFont('helvetica', 'bold');
    doc.text('Title:', margin, y);
    doc.setFont('helvetica', 'normal');
    const titleLines = doc.splitTextToSize(video.title, pageWidth - margin * 2 - 15);
    doc.text(titleLines, margin + 15, y);
    y += titleLines.length * 5 + 2;

    if (video.description) {
        doc.setFont('helvetica', 'bold');
        doc.text('Description:', margin, y);
        doc.setFont('helvetica', 'normal');
        const descLines = doc.splitTextToSize(video.description, pageWidth - margin * 2 - 25);
        doc.text(descLines, margin + 25, y);
        y += descLines.length * 5 + 5;
    }

    if (Object.keys(video.filters).length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('Filters:', margin, y);
        y += 7;
        doc.setFont('helvetica', 'normal');
        Object.entries(video.filters).forEach(([key, value]) => {
            const filterText = `${key}: ${Array.isArray(value) ? value.join(', ') : value}`;
            const filterLines = doc.splitTextToSize(filterText, pageWidth - margin * 2 - 5);
            doc.text(filterLines, margin + 5, y);
            y += filterLines.length * 5 + 1;
        });
        y += 5;
    }
    
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 10;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('AI Analysis Summary', margin, y);
    y += 10;
    
    doc.setFontSize(12);

    Object.keys(analysis.kpis).forEach(key => {
        if (y > 270) { 
            doc.addPage();
            y = drawHeader(doc, logos) + 10;
        }

        const kpi = analysis.kpis[key];
        const isScoredKpi = typeof kpi === 'object' && kpi !== null && 'score' in kpi;

        doc.setFont('helvetica', 'bold');
        doc.text(formatKpiKey(key), margin, y);
        
        if (isScoredKpi) {
            doc.text(`Score: ${(kpi as Kpi).score}/10`, pageWidth - margin, y, { align: 'right' });
        }
        y += 7;

        doc.setFont('helvetica', 'normal');
        const analysisText = isScoredKpi ? (kpi as Kpi).analysis : (kpi as string);
        const lines = doc.splitTextToSize(analysisText, pageWidth - margin * 2);
        doc.text(lines, margin, y);
        y += lines.length * 5 + 8;
    });

    if (chartImages && Object.keys(chartImages).length > 0) {
        doc.addPage();
        let chartPageY = drawHeader(doc, logos);
        chartPageY += 10;

        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('Análisis Visual', pageWidth / 2, chartPageY, { align: 'center' });
        chartPageY += 15;

        const chartWidth = (pageWidth - margin * 3) / 2; 
        const chartHeight = chartWidth; 

        const chartX1 = margin;
        const chartX2 = margin * 2 + chartWidth;
        let chartY = chartPageY;

        if (chartImages.radar && chartImages.bar) {
            doc.addImage(chartImages.radar, 'PNG', chartX1, chartY, chartWidth, chartHeight);
            doc.addImage(chartImages.bar, 'PNG', chartX2, chartY, chartWidth, chartHeight);
            chartY += chartHeight + margin;
        }

        if (chartImages.pie && chartImages.line) {
            doc.addImage(chartImages.pie, 'PNG', chartX1, chartY, chartWidth, chartHeight);
            doc.addImage(chartImages.line, 'PNG', chartX2, chartY, chartWidth, chartHeight);
        }
    }

    const safeTitle = video.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    doc.save(`${safeTitle}_mysteryshopperia_report.pdf`);
};

export const generateBulkAnalysisPdf = async (
    videos: Video[], 
    analyses: Record<string, AiAnalysis>,
    allChartImages?: Record<string, Record<string, string>>,
    company?: Company,
    generalAiSummary?: string
) => {
    const doc = new jsPDF();
    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerHeight = 10;
    
    const logos = { left: '', right: '' };
    if (company?.reportSettings?.headerLogos) {
        const { left, right } = company.reportSettings.headerLogos;
        const [leftBase64, rightBase64] = await Promise.all([
             left ? getBase64FromUrl(left) : Promise.resolve(''),
             right ? getBase64FromUrl(right) : Promise.resolve('')
        ]);
        logos.left = leftBase64;
        logos.right = rightBase64;
    }

    let y = drawHeader(doc, logos);

    const checkPageBreak = (spaceNeeded: number) => {
        if (y + spaceNeeded > pageHeight - footerHeight) {
            doc.addPage();
            y = drawHeader(doc, logos) + 10;
        }
    };

    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Consolidated MysteryShopperIA Report', pageWidth / 2, y, { align: 'center' });
    y += 10;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on: ${new Date().toLocaleDateString()} | Videos: ${videos.length}`, pageWidth / 2, y, { align: 'center' });
    y += 15;

    if (generalAiSummary) {
        checkPageBreak(60); 
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.line(margin, y, pageWidth - margin, y);
        y += 8;

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Resumen Ejecutivo Global (IA)', margin, y);
        y += 10;

        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        
        const cleanSummary = cleanMarkdown(generalAiSummary);
        const summaryLines = doc.splitTextToSize(cleanSummary, pageWidth - margin * 2);
        
        summaryLines.forEach((line: string) => {
            checkPageBreak(7);
            doc.text(line, margin, y);
            y += 6;
        });

        y += 10;
        doc.line(margin, y, pageWidth - margin, y);
        y += 15;
    }
    
    videos.forEach((video) => {
        const analysis = video.aiAnalysisId ? analyses[video.aiAnalysisId] : null;
        
        checkPageBreak(40); 

        doc.setDrawColor(150, 150, 150);
        doc.setLineWidth(0.1);
        doc.line(margin, y, pageWidth - margin, y);
        y += 8;

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        const titleLines = doc.splitTextToSize(`Video: ${video.title}`, pageWidth - margin * 2);
        doc.text(titleLines, margin, y);
        y += titleLines.length * 6 + 4;

        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');

        if (video.description) {
            const descLines = doc.splitTextToSize(video.description, pageWidth - margin * 2 - 25);
            checkPageBreak(descLines.length * 5 + 5);
            doc.setFont('helvetica', 'bold');
            doc.text('Description:', margin, y);
            doc.setFont('helvetica', 'normal');
            doc.text(descLines, margin + 25, y);
            y += descLines.length * 5 + 5;
        }

        if (!analysis) {
            checkPageBreak(15);
            doc.setFont('helvetica', 'italic');
            doc.text('Analysis not available for this video.', margin, y);
            y += 10;
        } else {
            Object.keys(analysis.kpis).forEach(key => {
                const kpi = analysis.kpis[key];
                const isScoredKpi = typeof kpi === 'object' && kpi !== null && 'score' in kpi;
                const analysisText = isScoredKpi ? (kpi as Kpi).analysis : (kpi as string);
                const lines = doc.splitTextToSize(analysisText, pageWidth - margin * 2);
                
                checkPageBreak(lines.length * 5 + 15); 

                doc.setFont('helvetica', 'bold');
                doc.text(formatKpiKey(key), margin, y);
                
                if (isScoredKpi) {
                    doc.text(`Score: ${(kpi as Kpi).score}/10`, pageWidth - margin, y, { align: 'right' });
                }
                y += 7;

                doc.setFont('helvetica', 'normal');
                doc.text(lines, margin, y);
                y += lines.length * 5 + 8;
            });

            const videoCharts = allChartImages?.[video.videoId];
            if (videoCharts && (videoCharts.radar || videoCharts.bar)) {
                 const chartHeight = 80;
                 checkPageBreak(chartHeight + 20);

                 y += 5;
                 doc.setFont('helvetica', 'bold');
                 doc.text('Performance Charts:', margin, y);
                 y += 10;

                 const chartWidth = (pageWidth - margin * 3) / 2;
                 const currentY = y;
                 
                 if (videoCharts.radar) {
                     doc.addImage(videoCharts.radar, 'PNG', margin, currentY, chartWidth, chartWidth);
                 }
                 if (videoCharts.bar) {
                     doc.addImage(videoCharts.bar, 'PNG', margin * 2 + chartWidth, currentY, chartWidth, chartWidth);
                 }
                 
                 y += chartWidth + 10;
            }
        }
        
        y += 5; 
    });

    doc.save('mysteryshopperia_consolidated_report.pdf');
};